import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useLocalParticipant } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { BackgroundBlur, VirtualBackground } from '@livekit/track-processors';
import { BackgroundEffectsControlProps } from './types';

// Background Effects Control Component (inside room context)
export const BackgroundEffectsControl: React.FC<BackgroundEffectsControlProps> = ({ 
    onEffectChange, 
    currentEffect, 
    showPanel, 
    onClose, 
    onVideoEnhancementChange,
    isMirrored,
    onMirrorToggle,
    isRecording,
    selectedCloudRes,
    setSelectedCloudRes,
    selectedLocalRes,
    setSelectedLocalRes,
    recordLayout,
    setRecordLayout,
    stopRecordingIfActive,
    startCloudRecording,
    startLocalRecording,
    recordingPreference,
    setRecordingPreference
}) => {
    const { localParticipant } = useLocalParticipant();
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentProcessor, setCurrentProcessor] = useState<any>(null);
    const [selectedBackground, setSelectedBackground] = useState<string>('');
    const [videoEnhancement, setVideoEnhancement] = useState<boolean>(false);
    const [enhancementLevel, setEnhancementLevel] = useState<number>(2);
    const [activeTab, setActiveTab] = useState<'effects' | 'recording' | 'avatar'>('effects');
    // const hasRestoredRef = useRef<boolean>(false); // Disabled with auto-restore

    // Built-in backgrounds - using local assets with correct Vite paths
    const builtInBackgrounds = [
        { 
            id: 'office', 
            name: 'Office', 
            url: '/assets/backgrounds/office.jpg',
            thumbnail: '/assets/backgrounds/office.jpg'
        },
        { 
            id: 'library', 
            name: 'Library', 
            url: '/assets/backgrounds/library.jpg',
            thumbnail: '/assets/backgrounds/library.jpg'
        },
        { 
            id: 'classroom', 
            name: 'Classroom', 
            url: '/assets/backgrounds/classroom.jpg',
            thumbnail: '/assets/backgrounds/classroom.jpg'
        },
        { 
            id: 'study', 
            name: 'Study Room', 
            url: '/assets/backgrounds/study.jpg',
            thumbnail: '/assets/backgrounds/study.jpg'
        },
        { 
            id: 'nature', 
            name: 'Nature', 
            url: '/assets/backgrounds/nature.jpg',
            thumbnail: '/assets/backgrounds/nature.jpg'
        },
        { 
            id: 'beach', 
            name: 'Beach', 
            url: '/assets/backgrounds/beach.jpg',
            thumbnail: '/assets/backgrounds/beach.jpg'
        },
        { 
            id: 'mountains', 
            name: 'Mountains', 
            url: '/assets/backgrounds/mountains.jpg',
            thumbnail: '/assets/backgrounds/mountains.jpg'
        },
        { 
            id: 'abstract', 
            name: 'Abstract', 
            url: '/assets/backgrounds/abstract.jpg',
            thumbnail: '/assets/backgrounds/abstract.jpg'
        }
    ];

    // Storage keys
    const STORAGE_KEYS = {
        BACKGROUND_EFFECT: 'meetplus_background_effect',
        BACKGROUND_URL: 'meetplus_background_url',
        VIDEO_ENHANCEMENT: 'meetplus_video_enhancement',
        ENHANCEMENT_LEVEL: 'meetplus_enhancement_level'
    };

    // Load preferences from localStorage
    const loadPreferences = () => {
        return {
            effect: localStorage.getItem(STORAGE_KEYS.BACKGROUND_EFFECT) || 'none',
            backgroundUrl: localStorage.getItem(STORAGE_KEYS.BACKGROUND_URL) || '',
            videoEnhancement: localStorage.getItem(STORAGE_KEYS.VIDEO_ENHANCEMENT) === 'true',
            enhancementLevel: parseInt(localStorage.getItem(STORAGE_KEYS.ENHANCEMENT_LEVEL) || '2')
        };
    };

    // Save preferences to localStorage
    const savePreferences = (prefs: any) => {
        Object.entries(prefs).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                localStorage.setItem(STORAGE_KEYS[key.toUpperCase()], value.toString());
            }
        });
    };

    // Preload image function for seamless background application
    const preloadImage = (imageUrl: string): Promise<void> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                console.log('✅ Background image preloaded:', imageUrl);
                resolve();
            };
            img.onerror = () => {
                console.warn('⚠️ Failed to preload background image:', imageUrl);
                resolve(); // Continue anyway
            };
            img.src = imageUrl;
        });
    };

    const handleEffectChange = async (effect: 'none' | 'blur' | 'image', imageUrl?: string) => {
        if (!localParticipant) {
            toast.error('Not connected to meeting');
            return;
        }

        // Prevent re-applying the same effect
        if (effect === currentEffect && effect !== 'image') {
            console.log('⚠️ Effect already applied, skipping');
            return;
        }

        // For image effects, only skip if it's the same image
        if (effect === 'image' && currentEffect === 'image' && selectedBackground === imageUrl) {
            console.log('⚠️ Same background image already applied, skipping');
            return;
        }

        try {
            setIsProcessing(true);
            
            // Get the video track
            const videoTrack = localParticipant.getTrackPublication(Track.Source.Camera)?.track;
            if (!videoTrack) {
                console.log('⏳ Video track not ready yet, will retry...');
                toast.error('Please enable your camera first');
                setIsProcessing(false);
                return;
            }
            
            console.log('🎥 Video track found:', {
                trackId: videoTrack.sid,
                isMuted: videoTrack.isMuted,
                kind: videoTrack.kind
            });

            // Only stop and restart if changing effects (not the same effect)
            if (currentProcessor && effect !== currentEffect) {
                console.log('🔄 Removing existing processor before applying new effect...');
                try {
                    await videoTrack.stopProcessor();
                    setCurrentProcessor(null);
                    // Small delay to ensure cleanup
                    await new Promise(resolve => setTimeout(resolve, 50));
                } catch (stopError) {
                    console.warn('⚠️ Error stopping processor:', stopError);
                    // Continue anyway - we want to apply the new effect
                }
            }

            // Apply new effect
            if (effect === 'blur') {
                console.log('🌫️ Applying background blur...');
                try {
                    const blurProcessor = BackgroundBlur(20);
                    await videoTrack.setProcessor(blurProcessor);
                    setCurrentProcessor(blurProcessor);
                    setSelectedBackground('');
                    console.log('✅ Background blur applied successfully');
                    toast.success('Background blur applied');
                } catch (error) {
                    console.error('Error applying blur:', error);
                    toast.error('Failed to apply blur effect');
                    setCurrentProcessor(null);
                }
            } else if (effect === 'image' && imageUrl) {
                console.log('🖼️ Applying background image...');
                try {
                    // Preload image before applying
                    await preloadImage(imageUrl);
                    
                    const imageProcessor = VirtualBackground(imageUrl);
                    await videoTrack.setProcessor(imageProcessor);
                    setCurrentProcessor(imageProcessor);
                    setSelectedBackground(imageUrl);
                    console.log('✅ Background image applied successfully');
                    toast.success('Background image applied');
                } catch (error) {
                    console.error('Error applying image:', error);
                    toast.error('Failed to apply background image');
                    setCurrentProcessor(null);
                }
            } else if (effect === 'none') {
                console.log('🚫 Removing background effect...');
                // Stop processor if one exists
                if (currentProcessor) {
                    try {
                        await videoTrack.stopProcessor();
                        console.log('🔄 Background processor stopped');
                    } catch (stopError) {
                        console.warn('⚠️ Error stopping processor:', stopError);
                    }
                }
                setCurrentProcessor(null);
                setSelectedBackground('');
                console.log('✅ Background effect removed successfully');
                toast.success('Background effect removed');
            }

            // Save preferences
            savePreferences({
                effect,
                backgroundUrl: imageUrl || '',
                videoEnhancement,
                enhancementLevel
            });

            onEffectChange(effect, imageUrl);
        } catch (error) {
            console.error('❌ Error applying background effect:', error);
            toast.error('Failed to apply background effect');
            setCurrentProcessor(null);
        } finally {
            setIsProcessing(false);
        }
    };

    // DISABLED: Auto-restore removed to prevent dark overlay flashing
    /*
    // Auto-restore last applied background effect on mount - ONCE ONLY
    useEffect(() => {
        // Only restore once, even if localParticipant changes
        if (hasRestoredRef.current) {
            console.log('⏭️ Background already restored once, skipping');
            return;
        }

        const restoreBackground = async () => {
            if (!localParticipant) {
                console.log('⏳ Local participant not ready yet');
                return;
            }

            const prefs = loadPreferences();
            const savedEffect = prefs.effect;
            const savedBackgroundUrl = prefs.backgroundUrl;

            if (savedEffect && savedEffect !== 'none') {
                console.log('🔄 Restoring saved background effect:', savedEffect, savedBackgroundUrl);
                
                // Wait for video track to be available
                let retries = 0;
                const maxRetries = 10;
                while (retries < maxRetries) {
                    const videoTrack = localParticipant.getTrackPublication(Track.Source.Camera)?.track;
                    if (videoTrack) {
                        await handleEffectChange(savedEffect as 'none' | 'blur' | 'image', savedBackgroundUrl || undefined);
                        console.log('✅ Background effect restored successfully');
                        hasRestoredRef.current = true; // Mark as restored
                        break;
                    }
                    await new Promise(resolve => setTimeout(resolve, 200));
                    retries++;
                }
                
                if (retries >= maxRetries) {
                    console.log('⚠️ Video track not available, skipping background restore');
                }
            } else {
                // No saved effect, mark as "restored" to prevent future attempts
                hasRestoredRef.current = true;
            }
        };

        restoreBackground();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localParticipant]); // Only run when localParticipant is available
    */

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const applyEnhancementFilter = (_enabled: boolean, _level: number) => {
        // Do nothing - video enhancement disabled to prevent flickering
        console.log('Video enhancement disabled to prevent flickering');
        onVideoEnhancementChange(false);
    };

    const changeEnhancementLevel = (level: number) => {
        setEnhancementLevel(level);
        localStorage.setItem('meetplus_enhancement_level', level.toString());
        if (videoEnhancement) {
            applyEnhancementFilter(true, level);
        }
    };

    const toggleVideoEnhancement = (enabled: boolean) => {
        setVideoEnhancement(enabled);
        localStorage.setItem('meetplus_video_enhancement', enabled.toString());
        applyEnhancementFilter(enabled, enhancementLevel);
        toast.success(enabled ? '✨ Video enhancement enabled' : '📷 Video enhancement disabled');
    };

    // Auto-apply saved background effect when participant is ready (preserved across meetings)
    useEffect(() => {
        if (!localParticipant) return;

        // Hard-disabled auto-apply to prevent any dimming or flickering issues
        // Users will apply effects manually when needed
        console.log('🚫 Auto-apply of effects disabled to prevent flickering');
    }, [localParticipant]);

    // DISABLED: Monitor video track changes to prevent screen darkening
    // useEffect(() => {
    //     if (!localParticipant) return;
    //     // No video track monitoring to prevent any darkening issues
    // }, [localParticipant]);
    useEffect(() => {
        return () => {
            // Only cleanup if we're actually unmounting the entire component
            // Don't cleanup when settings panel closes - we want effects to persist
            console.log('🧹 BackgroundEffectsControl component unmounting - preserving effects');
        };
    }, []);

    if (!showPanel) return null;

    return (
        <div className="absolute top-16 right-4 z-50 bg-gray-800 rounded-lg shadow-2xl border border-gray-700 h-[85vh] flex flex-col" style={{ width: '400px' }}>
            {/* Header with Tabs */}
                <div className="sticky top-0 bg-gray-800 z-10 border-b border-gray-700">
                    <div className="flex justify-between items-center px-4 pt-3 pb-2">
                        <h3 className="text-white font-semibold text-sm">Video Settings</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    
                    {/* Tab Buttons */}
                    <div className="flex px-4 pb-2 gap-2">
                        <button
                            onClick={() => setActiveTab('effects')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                activeTab === 'effects'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                        >
                            Effects
                        </button>
                        <button
                            onClick={() => setActiveTab('recording')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                activeTab === 'recording'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                        >
                            Recording
                        </button>
                    </div>
                </div>
            
            {/* Effects Tab */}
            {activeTab === 'effects' && (
                <div className="flex-1 overflow-y-auto">
                    <div className="p-4 space-y-4">
                        {/* Quick Effects */}
                        <div>
                            <h4 className="text-gray-300 text-xs font-semibold mb-2">QUICK EFFECTS</h4>
                            <div className="space-y-2">
                                {/* No Effect */}
                                <button
                                    onClick={() => handleEffectChange('none')}
                                    disabled={isProcessing}
                                    className={`w-full p-2.5 rounded-lg text-left transition-all ${
                                        currentEffect === 'none'
                                            ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded bg-gray-900 flex items-center justify-center flex-shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium">None</div>
                                            <div className="text-xs text-gray-400">Original background</div>
                                        </div>
                                    </div>
                                </button>

                                {/* Blur Effect */}
                                <button
                                    onClick={() => handleEffectChange('blur')}
                                    disabled={isProcessing}
                                    className={`w-full p-2.5 rounded-lg text-left transition-all ${
                                        currentEffect === 'blur'
                                            ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded bg-gray-900 flex items-center justify-center flex-shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="3"></circle>
                                                <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium">Blur</div>
                                            <div className="text-xs text-gray-400">Blur background</div>
                                        </div>
                                    </div>
                                </button>

                                {/* Video Flip Option */}
                                <button
                                    onClick={onMirrorToggle}
                                    className={`w-full p-2.5 rounded-lg text-left transition-all ${
                                        isMirrored
                                            ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded bg-gray-900 flex items-center justify-center flex-shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 2v20M12 2l-3 3m3-3l3 3"></path>
                                                <path d="M3 12h7m4 0h7"></path>
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium">Video Flip</div>
                                            <div className="text-xs text-gray-400">180° video rotation</div>
                                        </div>
                                    </div>
                                </button>

                                {/* Background Preview */}
                                <div className="mb-6">
                                    <div className="relative w-full h-40 rounded-lg overflow-hidden bg-gray-900">
                                        {currentEffect === 'image' && selectedBackground ? (
                                            <img 
                                                src={selectedBackground} 
                                                alt="Current background" 
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    console.error('Failed to load preview image:', selectedBackground);
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        ) : currentEffect === 'blur' ? (
                                            <div className="absolute inset-0 backdrop-blur-md bg-black/30"></div>
                                        ) : null}
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-20 h-20 rounded-full bg-gray-800 border-2 border-gray-700 flex items-center justify-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="12" cy="7" r="4"></circle>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Built-in Backgrounds */}
                                <div>
                                    <h4 className="text-gray-300 text-xs font-semibold mb-3">BUILT-IN BACKGROUNDS</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {builtInBackgrounds.map((bg) => (
                                            <button
                                                key={bg.id}
                                                onClick={() => handleEffectChange('image', bg.url)}
                                                disabled={isProcessing}
                                                className={`group relative overflow-hidden h-24 rounded-lg transition-all ${
                                                    currentEffect === 'image' && selectedBackground === bg.url
                                                        ? 'ring-2 ring-purple-500'
                                                        : 'ring-1 ring-gray-600 hover:ring-purple-400'
                                                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <img 
                                                    src={bg.thumbnail} 
                                                    alt={bg.name}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        console.error(`Failed to load background image: ${bg.name}`);
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                                                <div className="absolute bottom-2 left-2 text-white text-xs font-medium">
                                                    {bg.name}
                                                </div>
                                                {currentEffect === 'image' && selectedBackground === bg.url && (
                                                    <div className="absolute top-2 right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="20 6 9 17 4 12"></polyline>
                                                        </svg>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Custom Background Image */}
                                <div className="mt-4">
                                    <h4 className="text-gray-300 text-xs font-semibold mb-3">CUSTOM BACKGROUND</h4>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    const imageUrl = reader.result as string;
                                                    handleEffectChange('image', imageUrl);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        className="hidden"
                                        id="background-image-upload"
                                    />
                                    <div className="relative">
                                        <label
                                            htmlFor="background-image-upload"
                                            className={`group w-full h-24 rounded-lg border-2 border-dashed transition-all flex items-center justify-center cursor-pointer ${
                                                currentEffect === 'image' && !builtInBackgrounds.some(bg => bg.url === selectedBackground)
                                                    ? 'border-purple-500 bg-purple-500/10 hover:bg-purple-500/20'
                                                    : 'border-gray-600 hover:border-purple-400 hover:bg-purple-500/10'
                                            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            <div className="text-center">
                                                <div className="w-10 h-10 rounded-full bg-gray-700 group-hover:bg-gray-600 flex items-center justify-center mx-auto mb-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                        <polyline points="17 8 12 3 7 8"></polyline>
                                                        <line x1="12" y1="3" x2="12" y2="15"></line>
                                                    </svg>
                                                </div>
                                                <div className="text-sm font-medium">Upload Image</div>
                                                <div className="text-xs text-gray-400 mt-1">Click to choose a file</div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Video Enhancement */}
                        <div>
                            <h4 className="text-gray-300 text-xs font-semibold mb-2">VIDEO ENHANCEMENT</h4>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-300">Enable Enhancement</span>
                                    <button
                                        onClick={() => toggleVideoEnhancement(!videoEnhancement)}
                                        className={`w-12 h-6 rounded-full transition-colors ${
                                            videoEnhancement ? 'bg-purple-600' : 'bg-gray-600'
                                        }`}
                                    >
                                        <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                                            videoEnhancement ? 'translate-x-6' : 'translate-x-0.5'
                                        }`}></div>
                                    </button>
                                </div>
                                
                                {videoEnhancement && (
                                    <div>
                                        <label className="text-xs text-gray-400 block mb-2">Enhancement Level</label>
                                        <div className="space-y-2">
                                            {[1, 2, 3].map((level) => (
                                                <button
                                                    key={level}
                                                    onClick={() => changeEnhancementLevel(level)}
                                                    className={`w-full p-2 rounded text-xs transition-all ${
                                                        enhancementLevel === level
                                                            ? 'bg-purple-600 text-white'
                                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                    }`}
                                                >
                                                    {level === 1 ? 'Light' : level === 2 ? 'Medium (Recommended)' : 'Strong'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Recording Tab */}
            {activeTab === 'recording' && (
                <div className="flex-1 overflow-y-auto">
                    <div className="p-4 space-y-4">
                        <h4 className="text-gray-300 text-xs font-semibold mb-2">RECORDING OPTIONS</h4>
                        
                        {/* Recording Status */}
                        <div className="bg-gray-700 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-300">Recording Status</span>
                                <div className={`flex items-center gap-2 ${isRecording ? 'text-red-400' : 'text-gray-400'}`}>
                                    <div className={`w-2 h-2 rounded-full ${isRecording ? 'bg-red-400 animate-pulse' : 'bg-gray-400'}`}></div>
                                    <span className="text-xs">{isRecording ? 'Recording' : 'Not Recording'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Recording Preference */}
                        <div className="space-y-3">
                            <h4 className="text-gray-300 text-xs font-semibold">RECORDING TYPE</h4>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => setRecordingPreference('local')}
                                    className={`p-3 rounded-lg border-2 transition-all ${
                                        recordingPreference === 'local'
                                            ? 'border-blue-500 bg-blue-500/20'
                                            : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                    }`}
                                >
                                    <div className="flex flex-col items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={recordingPreference === 'local' ? '#3b82f6' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 2v8L8 6l4 4-4-4 4 4"></path>
                                            <rect x="4" y="12" width="16" height="10" rx="2"></rect>
                                        </svg>
                                        <span className="text-xs text-gray-300">Local</span>
                                        <span className="text-[10px] text-gray-400">Download</span>
                                    </div>
                                </button>

                                <button
                                    onClick={() => setRecordingPreference('cloud')}
                                    className={`p-3 rounded-lg border-2 transition-all ${
                                        recordingPreference === 'cloud'
                                            ? 'border-blue-500 bg-blue-500/20'
                                            : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                    }`}
                                >
                                    <div className="flex flex-col items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={recordingPreference === 'cloud' ? '#3b82f6' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                            <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                        </svg>
                                        <span className="text-xs text-gray-300">Cloud</span>
                                        <span className="text-[10px] text-gray-400">Server</span>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Cloud Recording Settings */}
                        <div className="space-y-3">
                            <h4 className="text-gray-300 text-xs font-semibold">CLOUD RECORDING SETTINGS</h4>
                            
                            {/* Resolution Selection - Visual */}
                            <div>
                                <label className="text-sm text-gray-300 block mb-3">Resolution</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {/* 480p */}
                                    <button
                                        onClick={() => setSelectedCloudRes('480p')}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            selectedCloudRes === '480p'
                                                ? 'border-purple-500 bg-purple-500/20'
                                                : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-6 bg-gray-600 rounded-sm relative overflow-hidden">
                                                <div className="absolute inset-0 bg-gray-500 rounded-sm"></div>
                                                <div className="absolute top-0.5 left-0.5 w-1 h-1 bg-green-400 rounded-sm"></div>
                                            </div>
                                            <span className="text-xs text-gray-300">480p</span>
                                            <span className="text-[10px] text-gray-400">Low</span>
                                        </div>
                                    </button>

                                    {/* 720p */}
                                    <button
                                        onClick={() => setSelectedCloudRes('720p')}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            selectedCloudRes === '720p'
                                                ? 'border-purple-500 bg-purple-500/20'
                                                : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-6 bg-gray-600 rounded-sm relative overflow-hidden">
                                                <div className="absolute inset-0 bg-gray-500 rounded-sm"></div>
                                                <div className="absolute top-0.5 left-0.5 w-2 h-1.5 bg-blue-400 rounded-sm"></div>
                                            </div>
                                            <span className="text-xs text-gray-300">720p</span>
                                            <span className="text-[10px] text-gray-400">Medium</span>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* Layout Selection - Visual like Zoom */}
                            <div>
                                <label className="text-sm text-gray-300 block mb-3">Recording Layout</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {/* Auto Layout */}
                                    <button
                                        onClick={() => setRecordLayout('auto')}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            recordLayout === 'auto'
                                                ? 'border-purple-500 bg-purple-500/20'
                                                : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-6 bg-gray-600 rounded-sm relative overflow-hidden">
                                                <div className="absolute inset-0 bg-gray-500 rounded-sm"></div>
                                                <div className="absolute top-0.5 left-0.5 w-2 h-1.5 bg-blue-400 rounded-sm"></div>
                                            </div>
                                            <span className="text-xs text-gray-300">Auto</span>
                                        </div>
                                    </button>

                                    {/* Picture-in-Picture Circle */}
                                    <button
                                        onClick={() => setRecordLayout('pip-circle')}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            recordLayout === 'pip-circle'
                                                ? 'border-purple-500 bg-purple-500/20'
                                                : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-6 bg-gray-600 rounded-sm relative overflow-hidden">
                                                <div className="absolute inset-0 bg-gray-500 rounded-sm"></div>
                                                <div className="absolute top-0.5 right-0.5 w-2 h-2 bg-blue-400 rounded-full"></div>
                                            </div>
                                            <span className="text-xs text-gray-300">PiP Circle</span>
                                        </div>
                                    </button>

                                    {/* Side by Side */}
                                    <button
                                        onClick={() => setRecordLayout('side-by-side')}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            recordLayout === 'side-by-side'
                                                ? 'border-purple-500 bg-purple-500/20'
                                                : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-6 bg-gray-600 rounded-sm relative overflow-hidden">
                                                <div className="absolute left-0 top-0 w-4 h-6 bg-gray-500"></div>
                                                <div className="absolute right-0 top-0 w-4 h-6 bg-blue-400"></div>
                                            </div>
                                            <span className="text-xs text-gray-300">Side by Side</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Local Recording Settings */}
                        <div className="space-y-3">
                            <h4 className="text-gray-300 text-xs font-semibold">LOCAL RECORDING SETTINGS</h4>
                            
                            {/* Resolution Selection - Visual */}
                            <div>
                                <label className="text-sm text-gray-300 block mb-3">Resolution</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {/* 720p */}
                                    <button
                                        onClick={() => setSelectedLocalRes('720p')}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            selectedLocalRes === '720p'
                                                ? 'border-purple-500 bg-purple-500/20'
                                                : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-6 bg-gray-600 rounded-sm relative overflow-hidden">
                                                <div className="absolute inset-0 bg-gray-500 rounded-sm"></div>
                                                <div className="absolute top-0.5 left-0.5 w-2 h-1.5 bg-blue-400 rounded-sm"></div>
                                            </div>
                                            <span className="text-xs text-gray-300">720p</span>
                                            <span className="text-[10px] text-gray-400">Medium</span>
                                        </div>
                                    </button>

                                    {/* 1080p */}
                                    <button
                                        onClick={() => setSelectedLocalRes('1080p')}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            selectedLocalRes === '1080p'
                                                ? 'border-purple-500 bg-purple-500/20'
                                                : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-6 bg-gray-600 rounded-sm relative overflow-hidden">
                                                <div className="absolute inset-0 bg-gray-500 rounded-sm"></div>
                                                <div className="absolute top-0.5 left-0.5 w-3 h-2 bg-red-400 rounded-sm"></div>
                                            </div>
                                            <span className="text-xs text-gray-300">1080p</span>
                                            <span className="text-[10px] text-gray-400">High</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Recording Controls */}
                        <div className="space-y-3 mt-6">
                            <h4 className="text-gray-300 text-xs font-semibold">RECORDING CONTROLS</h4>
                            {!isRecording ? (
                                <div className="space-y-2">
                                    <button
                                        onClick={startCloudRecording}
                                        className="w-full p-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 2l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z"></path>
                                            <path d="M12 15v7"></path>
                                        </svg>
                                        Start Cloud Recording
                                    </button>
                                    <button
                                        onClick={startLocalRecording}
                                        className="w-full p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 2v8L8 6l4 4-4-4 4 4"></path>
                                            <rect x="4" y="12" width="16" height="10" rx="2"></rect>
                                        </svg>
                                        Start Local Recording
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={stopRecordingIfActive}
                                    className="w-full p-3 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="6" y="6" width="12" height="12" rx="2"></rect>
                                    </svg>
                                    Stop Recording
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};
